import { Link } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";

const STEPS = [
  {
    step: "01",
    title: "Enter Your Buy Price",
    description:
      "Type in what you paid for the item — the full cost including any buyer fees or shipping you paid when you acquired it. Whether it&apos;s a trading card, a pair of sneakers, or a vintage jacket, this is your true cost basis, and Trackly keeps it precise.",
    badge: "Input",
    highlight: "Your cost basis is everything you paid — not just the sticker price.",
  },
  {
    step: "02",
    title: "Choose Your Selling Platform",
    description:
      "Select where you&apos;re selling: eBay, StockX, GOAT, Poshmark, Depop, Whatnot, Mercari, Facebook Marketplace, and more. Trackly has every platform&apos;s current fee structure pre-loaded, so you never have to look it up.",
    badge: "Platform",
    highlight: "eBay alone has 5 different fee tiers. Poshmark takes 20% over $15. Trackly knows them all.",
  },
  {
    step: "03",
    title: "Add Your Sale Price & Shipping",
    description:
      "Enter what you sold the item for, plus any shipping you&apos;re charging or absorbing. Whether you offer free shipping or charge the buyer, Trackly factors in the real cost to you.",
    badge: "Sale Details",
    highlight: "Free shipping isn&apos;t free — it comes out of your margin.",
  },
  {
    step: "04",
    title: "See Your Real Net Profit & ROI",
    description:
      "Instantly see your true net profit after all fees, shipping, and costs are deducted. Trackly also shows your ROI percentage so you know exactly how hard your money worked on that sale.",
    badge: "Results",
    highlight: "This is the number that actually matters. Now you know it.",
  },
];

export default function DemoSteps() {
  const { isDark } = useTheme();

  return (
    <section id="steps" className={`py-20 px-8 lg:px-16 transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-[#f8f8f8]"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-20">
          {STEPS.map((step, idx) => (
            <div
              key={step.step}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start"
            >
              {/* Text */}
              <div className={idx % 2 === 1 ? "lg:order-2" : "lg:order-1"}>
                <div className="flex items-center gap-3 mb-6">
                  <span className={`font-syne font-bold text-5xl ${isDark ? "text-[#282828]" : "text-[#e0e0e0]"}`}>{step.step}</span>
                  <span className={`border rounded-full px-3 py-1 font-inter text-xs uppercase tracking-widest ${isDark ? "bg-white/5 border-white/10 text-[#666666]" : "bg-black/4 border-black/8 text-[#888888]"}`}>
                    {step.badge}
                  </span>
                </div>
                <h3 className={`font-syne font-bold text-3xl lg:text-4xl leading-tight mb-5 ${isDark ? "text-white" : "text-[#111111]"}`}>
                  {step.title}
                </h3>
                <p
                  className={`font-inter text-base leading-relaxed mb-6 ${isDark ? "text-[#555555]" : "text-[#666666]"}`}
                  dangerouslySetInnerHTML={{ __html: step.description }}
                />
                <div className={`border rounded-xl px-5 py-3.5 ${isDark ? "bg-white/3 border-white/8" : "bg-black/3 border-black/8"}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className={`ri-lightbulb-line text-sm ${isDark ? "text-[#666666]" : "text-[#888888]"}`} />
                    </div>
                    <p
                      className={`font-inter text-sm italic ${isDark ? "text-[#666666]" : "text-[#777777]"}`}
                      dangerouslySetInnerHTML={{ __html: step.highlight }}
                    />
                  </div>
                </div>
              </div>

              {/* Visual */}
              <div className={idx % 2 === 1 ? "lg:order-1" : "lg:order-2"}>
                <div className={`border rounded-2xl p-6 ${isDark ? "bg-[#0d0d0d] border-white/8" : "bg-white border-black/8"}`}>
                  {idx === 0 && (
                    <div className="flex flex-col gap-4">
                      <p className={`font-inter text-xs uppercase tracking-widest mb-2 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>Cost Basis</p>
                      {[
                        { label: "Purchase Price", placeholder: "$45.00", filled: true },
                        { label: "Buyer Fees (if any)", placeholder: "$0.00", filled: false },
                        { label: "Acquisition Shipping", placeholder: "$4.50", filled: true },
                      ].map((f) => (
                        <div key={f.label}>
                          <p className={`font-inter text-xs mb-1.5 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>{f.label}</p>
                          <div className={`border rounded-xl px-4 py-3 font-inter text-sm ${
                            f.filled
                              ? isDark ? "border-white/10 bg-[#111111] text-[#999999]" : "border-black/10 bg-[#f5f5f5] text-[#555555]"
                              : isDark ? "border-white/4 bg-[#080808] text-[#444444]" : "border-black/4 bg-[#eeeeee] text-[#aaaaaa]"
                          }`}>
                            {f.placeholder}
                          </div>
                        </div>
                      ))}
                      <div className={`border-t pt-4 flex items-center justify-between ${isDark ? "border-white/5" : "border-black/5"}`}>
                        <span className={`font-inter text-xs ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>Total Cost Basis</span>
                        <span className={`font-syne font-bold ${isDark ? "text-white" : "text-[#111111]"}`}>$49.50</span>
                      </div>
                    </div>
                  )}
                  {idx === 1 && (
                    <div className="flex flex-col gap-4">
                      <p className={`font-inter text-xs uppercase tracking-widest mb-2 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>Select Platform</p>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { name: "eBay", fee: "13.25%", active: true },
                          { name: "StockX", fee: "9–10%", active: false },
                          { name: "Poshmark", fee: "20%", active: false },
                          { name: "GOAT", fee: "9.5%", active: false },
                          { name: "Depop", fee: "10%", active: false },
                          { name: "Mercari", fee: "10%", active: false },
                        ].map((p) => (
                          <div
                            key={p.name}
                            className={`border rounded-xl px-4 py-3 cursor-pointer ${
                              p.active
                                ? isDark ? "border-white/20 bg-white/5" : "border-black/20 bg-black/5"
                                : isDark ? "border-white/5 bg-[#111111]" : "border-black/5 bg-[#f5f5f5]"
                            }`}
                          >
                            <p className={`font-inter text-sm ${p.active ? (isDark ? "text-white" : "text-[#111111]") : (isDark ? "text-[#555555]" : "text-[#777777]")}`}>{p.name}</p>
                            <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-[#444444]" : "text-[#aaaaaa]"}`}>Fee: {p.fee}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {idx === 2 && (
                    <div className="flex flex-col gap-4">
                      <p className={`font-inter text-xs uppercase tracking-widest mb-2 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>Sale Details</p>
                      {[
                        { label: "Sale Price", value: "$120.00" },
                        { label: "Shipping Charged to Buyer", value: "$0.00 (Free shipping)" },
                        { label: "Actual Shipping Cost to You", value: "$8.50" },
                      ].map((f) => (
                        <div key={f.label}>
                          <p className={`font-inter text-xs mb-1.5 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>{f.label}</p>
                          <div className={`border rounded-xl px-4 py-3 font-inter text-sm ${isDark ? "border-white/10 bg-[#111111] text-[#999999]" : "border-black/10 bg-[#f5f5f5] text-[#555555]"}`}>
                            {f.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {idx === 3 && (
                    <div className="flex flex-col gap-4">
                      <p className={`font-inter text-xs uppercase tracking-widest mb-2 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>Your Results</p>
                      <div className="flex flex-col gap-2">
                        {[
                          { label: "Sale Price", value: "$120.00", type: "neutral" },
                          { label: "Cost Basis", value: "–$49.50", type: "deduct" },
                          { label: "eBay Fee (13.25%)", value: "–$15.90", type: "deduct" },
                          { label: "Payment Processing (2.9%)", value: "–$3.57", type: "deduct" },
                          { label: "Shipping Cost", value: "–$8.50", type: "deduct" },
                        ].map((r) => (
                          <div key={r.label} className={`flex items-center justify-between py-2 border-b ${isDark ? "border-white/4" : "border-black/4"}`}>
                            <span className={`font-inter text-xs ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>{r.label}</span>
                            <span className={`font-inter text-xs ${r.type === "deduct" ? (isDark ? "text-[#555555]" : "text-[#888888]") : (isDark ? "text-[#888888]" : "text-[#444444]")}`}>{r.value}</span>
                          </div>
                        ))}
                      </div>
                      <div className={`border rounded-xl px-5 py-4 ${isDark ? "bg-white/5 border-white/10" : "bg-black/4 border-black/8"}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={`font-inter text-sm ${isDark ? "text-[#777777]" : "text-[#666666]"}`}>Net Profit</span>
                          <span className={`font-syne font-bold text-2xl ${isDark ? "text-white" : "text-[#111111]"}`}>$42.53</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`font-inter text-xs ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>Return on Investment</span>
                          <span className={`font-inter text-sm ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>+85.9% ROI</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <p className={`font-inter text-sm mb-6 ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>
            Ready to know your real margins on every sale?
          </p>
          <Link to="/product">
            <button className={`font-inter font-medium text-sm px-10 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2 ${isDark ? "bg-white text-black hover:bg-[#e8e8e8]" : "bg-[#111111] text-white hover:bg-[#2a2a2a]"}`}>
              Get Early Access Free
              <i className="ri-arrow-right-line" />
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}
